import {
  ClaimCompoundCode,
  DispensingSite,
  QuestionnaireResponseSetCreateRequest,
  ReviewType,
} from "capitalrx-types";

import { CommunicationType, CommunicationStatus } from "capitalrx-types";


interface Deadline {
  id: number;
  title: string;
  deadline_type: string;
  due_at: string;
  completed_at: string;
}

interface CaseOutcome {
  case_id: string;
  rn: string;
  outcome: string;
  secondary_outcome: string;
  rationale: string;
  rationale_translation: string;
  identity: string;
  determiner: string;
  created_at: string;
  updated_at: string;
  denial_type: string;
  denial_reason: string;
  close_case_reason: string;
  close_case_rationale: string;
  close_case_rationale_translation: string;
  approval_addendum: string;
  approval_addendum_translation: string;
  facility_fax: boolean;
  member_mail: boolean;
  pharmacy_fax: boolean;
  pharmacy_mail: boolean;
  prescriber_fax: boolean;
  prescriber_mail: boolean;
  representative_mail: boolean;
  pharmacy_npi: string;
  is_auto_decision: boolean;
}

interface Event {
  event_type: string;
  at: string;
  identity: string;
}

interface CaseDocument {
  uuid: string;
  tenant_id: string;
  rn: string;
  filename: string;
  method: string;
  document_type: string;
  created_at: string;
  url_for_upload: string;
}

interface CaseLetter {
  uuid: string;
  filename: string;
  letter_template_uuid: string;
  recipient_type: string;
  created_at: string;
}

interface CaseInboundFax {
  uuid: string;
  filename: string;
  created_at: string;
  document_type: string;
}

interface CommunicationDetails {
  id: number;
  communication_type: CommunicationType;
  status: CommunicationStatus;
  created_at: string;
  updated_at: string;
  attempt_number: number;
}

interface Communication {
  communication: CommunicationDetails;
}

interface Document {
  uuid: string;
  result_type: string;
  document?: CaseDocument;
  letter?: CaseLetter;
  inbound_fax?: CaseInboundFax;
  communications: Communication[];
}

export interface PACaseRequest {
  case_id?: number;
  drug_gpi?: string;
  prescriber_npi: string;
  external_member_id: string;
  member_date_of_birth: string;
}

export interface PADocumentAttachRequest {
  case_id: number;
  prescriber_npi: string;
  external_member_id: string;
  member_date_of_birth: string;
  document_uuids: string[];
}

export interface PADocumentCreateRequest {
  case_id: number;
  prescriber_npi: string;
  external_member_id: string;
  member_date_of_birth: string;
  filename: string;
}

interface PACaseProvider {
  first_name: string;
  last_name: string;
  credential: string;
  fax_number?: string;
}

export interface PACaseResponse {
  external_member_id?: string; // Not a dupe, this is for PA Cases when user is logged in and directly getting case with case id
  case_id: number;
  case_outcome: CaseOutcome;
  completed_at: string;
  date_of_service: string;
  deadlines: Deadline[];
  documents: Document[];
  drug_ndc: string;
  drug_name: string;
  drug_dosage: string;
  drug_dosage_form: string;
  drug_quantity: number;
  drug_days_supply: number;
  drug_gpi: string;
  due_at: string;
  effective_date: string;
  events: Event[];
  expiration_date: string;
  is_urgent: boolean;
  last_outreach_sent_at: string;
  last_outreach_method: string;
  member_external_id: string;
  member_date_of_birth: string;
  next_deadline_at: string;
  next_deadline_type: string;
  num_outreach_attempts: number;
  outreach_due_at: string;
  outreach_started_at: string;
  outreach_ended_at: string;
  prescriber_npi: string;
  queue: string;
  requesting_provider_contact_card: PACaseProvider;
  re_completed_at: string;
  status: string;
}

export interface PACaseValidateRequest {
  external_member_id: string;
  member_first_name: string;
  member_last_name: string;
  member_date_of_birth: string;
  ndc: string;
  quantity_dispensed: number;
  days_supply: number;
  daw_code: string;
}

export enum ValidateResultType {
  DrugNotFound = "drug_not_found",
  Nonformulary = "nonformulary_exception",
  PatientSearchFail = "patient_search_fail",
  PatientNotFound = "patient_not_found",
  PrescriberNotFound = "prescriber_not_found",
  PrescriberSearchFail = "prescriber_search_fail",
  PANotRequired = "pa_not_required",
  PARequired = "pa_required",
  StepTherapyException = "step_therapy_exception",
  TestClaimFail = "test_claim_fail",
}

export interface PACaseValidateTestClaim {
  client_id: string;
  compound_code: ClaimCompoundCode;
  external_account_id: string;
  external_group_id: string;
  external_member_id: string;
  drug_dosage_form: string;
  drug_dosage: string;
  drug_gpi: string;
  drug_multi_source: string;
  drug_name: string;
  group_id: string;
  person_code: string;
  programs: string[];
  reject_codes: string[];
  prescriber_first: string;
  prescriber_last: string;
  daw_code: string;
  dispensing_sites: string[];
}

export interface PACaseValidateResponse {
  result: ValidateResultType;
  test_claim: PACaseValidateTestClaim;
}

export interface PACaseCreateResponse {
  case_id: string;
}

export interface PACaseCreateRequest {
  client_id: string;
  external_member_id: string;
  member_first_name: string;
  member_last_name: string;
  member_date_of_birth: string;
  person_code: string;
  is_urgent: boolean;
  review_type: ReviewType;
  dispensing_site: DispensingSite;
  drug_ndc: string;
  drug_name: string;
  drug_quantity: number;
  drug_days_supply: number;
  questionnaire_responses: QuestionnaireResponseSetCreateRequest[];
  provider_npi: string;
  facility_npi: string;
  daw_code: string;
}

export interface PACaseListRequest {
  is_action_required: boolean;
  case_outcome: string[];
  query_term: string;
  results_per_page: number;
  page_number: number;
  external_member_id?: string[];
  drug_gpi?: string;
}

interface PACaseListCase {
  case_id: string;
  outcome: string;
  is_complete: boolean;
  status: string;
  member_first_name: string;
  member_last_name: string;
  drug_name: string;
  date_of_service: string;
  effective_date: string;
  expiration_date: string;
  queue: string;
}
interface PACaseListOutcome {
  outcome: string;
}

export interface PACaseListResult {
  case: PACaseListCase;
  case_outcome: PACaseListOutcome;
}

export interface PACaseListResponse {
  results: PACaseListResult[]
}
